import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@core/lib/utils'

interface Props {
  score: number
  className?: string
}

const SCORE_CONFIG = [
  { key: 'veryWeak', color: 'bg-red-700', textColor: 'text-red-700' },
  { key: 'weak', color: 'bg-red-400', textColor: 'text-red-400' },
  { key: 'medium', color: 'bg-orange-400', textColor: 'text-orange-400' },
  { key: 'good', color: 'bg-green-400', textColor: 'text-green-400' },
  { key: 'veryGood', color: 'bg-green-600', textColor: 'text-green-600' },
] as const

export default function PasswordStrengthBar({ score, className }: Props) {
  const { t } = useTranslation();
  const clampedScore: number = Math.min(Math.max(score, 0), 4);
  const config = SCORE_CONFIG[clampedScore];
  const percentage: number = (clampedScore / 4) * 100;

  return (
    <div className={cn('w-full text-right', className)}>
      <AnimatePresence mode="wait">
        <motion.span
          key={config.key}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className={cn('text-sm font-medium mb-1 block', config.textColor)}
        >
          {t(`passwordStrength.${config.key}`)}
        </motion.span>
      </AnimatePresence>

      <div className="relative h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          className={cn('h-full rounded-full', config.color)}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{
            type: 'spring',
            stiffness: 100,
            damping: 15,
            mass: 0.5
          }}
        />
      </div>

      {/* Strength indicator dots */}
      <div className="flex justify-between mt-1.5 px-0.5">
        {SCORE_CONFIG.map((_, index) => (
          <motion.div
            key={index}
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              index <= clampedScore ? config.color : 'bg-gray-300 dark:bg-gray-600'
            )}
            initial={{ scale: 0 }}
            animate={{ scale: index <= clampedScore ? 1 : 0.7 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20,
              delay: index * 0.05
            }}
          />
        ))}
      </div>
    </div>
  )
}